import ServiceExec from '@/common/utils/ServiceExec';
import { b2bBoard, businessUser, common, eventCoupon, qrUser, signboard, signboardStats } from '@/common/service/apiList';
import { cloneDeep } from 'lodash';
import { asFormData } from '@/common/utils/module/FormDataSupport';
import store from '@/store';
import { isEmpty } from '@/common/utils/Util';
import { showTextModal } from '@/common/utils/ModalState';
import { defaultPaging } from '@/common/constants/ConstCode';
import { resizeImageFile } from '@/common/utils/imageResize';
import { trackFeature } from '@/common/utils/retentionTracker';
import { logSearch } from '@/common/utils/actionLog'; // 261028 행동 로그 — 검색어(무엇을 찾는지)

// 261006 main 판 — dev 는 비즈매칭 bizmatchCore.mjs 의 errorText 를 쓰지만 main 엔 그 모듈이 없어 같은 함수를 여기 둔다.
//   서버 거절 문구(평문) 그대로, HTML 문서(502·점검 페이지)면 fallback. 공용 알림 모달이 v-html 로 그려 < > & 를 바꾼다.
const HTML_ESCAPE = { '&': '&amp;', '<': '&lt;', '>': '&gt;' };
const errorText = (data, fallback) => {
  const raw = typeof data === 'string' ? data : data && data.message;
  const text = typeof raw === 'string' ? raw.trim() : '';
  if (!text || text.startsWith('<')) return fallback;
  return text.replace(/[&<>]/g, c => HTML_ESCAPE[c]);
};

// 260428 오후회의2: File 단건/배열을 안전하게 리사이즈 (실패하면 원본 폴백)
const tryResize = async (file) => {
  if (!(file instanceof File)) return file;
  try {
    return await resizeImageFile(file, { maxWidth: 1600, maxHeight: 1200, quality: 0.82 });
  } catch (e) {
    console.warn('[business] 이미지 리사이즈 실패, 원본 사용:', e);
    return file;
  }
};
const tryResizeList = async (list) => {
  if (!Array.isArray(list)) return list;
  return Promise.all(list.map(tryResize));
};

export default {
  getCategoryList: async ({ commit }) => {
    const { status, data } = await ServiceExec.get(common.getCategoryList);
    if (status !== ServiceExec.status.OK) return;
    commit('categoryList', data);
  },
  // 260602: 비즈니스 소개 5지선다 답변 → GPT 서술형 소개문 생성. 실패 시 null 반환(컴포넌트가 폴백 안내).
  generateBusinessIntro: async (_ctx, payload) => {
    try {
      const { status, data } = await ServiceExec.post(businessUser.generateIntro, payload);
      if (status !== ServiceExec.status.OK) return null;
      return data?.introduce ?? null;
    } catch (e) {
      console.warn('[business] 소개문 생성 실패:', e);
      return null;
    }
  },
  // 260604: 업종별 해시태그 AI 생성. 실패 시 빈 배열(호출처가 코드 fallback).
  aiBusinessTags: async (_ctx, payload) => {
    try {
      const { status, data } = await ServiceExec.post(businessUser.aiTags, payload);
      if (status !== ServiceExec.status.OK) return [];
      return data ?? [];
    } catch (e) {
      console.warn('[business] AI 태그 생성 실패:', e);
      return [];
    }
  },
  // 260629: 사장측 RAG Slice1 — 기업 성장 AI 분석 생성(온디맨드). 실패 시 null.
  analyzeGrowth: async (_ctx, { businessNo, force = false }) => {
    try {
      const url = `${businessUser.growthAnalyze}/${businessNo}${force ? '?force=true' : ''}`;
      const { status, data } = await ServiceExec.post(url, {});
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) {
      console.warn('[business] 성장 분석 실패:', e);
      return null;
    }
  },
  // 260629: 기업 성장 AI 분석 최근 결과(캐시) 조회. 없으면 null.
  getGrowthReport: async (_ctx, businessNo) => {
    try {
      const { status, data } = await ServiceExec.get(`${businessUser.growthLatest}/${businessNo}`);
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) {
      console.warn('[business] 성장 분석 조회 실패:', e);
      return null;
    }
  },
  // 260630: 일반 분석(사실 카드). {cards:[{key,title,value,hint,tone}], lowData} 반환. LLM 0콜. 실패 시 null.
  getGeneralAnalysis: async (_ctx, businessNo) => {
    try {
      const { status, data } = await ServiceExec.get(`${businessUser.growthGeneral}/${businessNo}`);
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) {
      console.warn('[business] 일반 분석 조회 실패:', e);
      return null;
    }
  },
  // 260629→260630: B2B 협업 추천. {matches, status(NO_PROFILE/NO_MATCH/OK), waiting} 반환. 실패 시 null.
  matchB2B: async (_ctx, businessNo) => {
    try {
      const { status, data } = await ServiceExec.post(`${businessUser.growthB2b}/${businessNo}`, {});
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) {
      console.warn('[business] B2B 협업 추천 실패:', e);
      return null;
    }
  },
  // 260630: 보완 업체 없을 때 대기 신청(수요 신호). 성공 true.
  requestB2bWaitlist: async (_ctx, businessNo) => {
    try {
      const { status } = await ServiceExec.post(`${businessUser.growthB2bWaitlist}/${businessNo}`, {});
      return status === ServiceExec.status.OK;
    } catch (e) {
      console.warn('[business] B2B 대기 신청 실패:', e);
      return false;
    }
  },
  // 260630: 가게 DNA 선답지 조회(prefill). 실패/없음 시 null.
  getBusinessProfile: async (_ctx, businessNo) => {
    try {
      const { status, data } = await ServiceExec.get(`${businessUser.growthProfile}/${businessNo}`);
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) {
      console.warn('[business] 가게 DNA 조회 실패:', e);
      return null;
    }
  },
  // 260630: 가게 DNA 선답지 저장(upsert). 성공 시 응답, 실패 시 null.
  saveBusinessProfile: async (_ctx, { businessNo, answers }) => {
    try {
      const { status, data } = await ServiceExec.post(`${businessUser.growthProfile}/${businessNo}`, { answers });
      if (status !== ServiceExec.status.OK) return null;
      return data ?? null;
    } catch (e) {
      console.warn('[business] 가게 DNA 저장 실패:', e);
      return null;
    }
  },
  getSignboardFeed: async ({ commit, getters }) => {
    const { latitude, longitude } = getters['getCoordinate'];
    // 260428: 거리순 기본 (좌표 없으면 BE가 random fallback)
    const params = { latitude, longitude, page: 1, limit: 10, sort: 'distance' };
    const { status, data } = await ServiceExec.get(signboard.getFeed, params);
    if (status !== ServiceExec.status.OK) return false;
    commit('signboardFeedList', data.list ?? []);
    return true;
  },
  // 간판 상세 조회 (businessNo 단위) — 배터리 관리 화면용
  getSignboardDetail: async ({ commit }, businessNo) => {
    const { status, data } = await ServiceExec.get(signboard.getDetail(businessNo));
    if (status !== ServiceExec.status.OK) return null;
    commit('signboardDetail', data ?? {});
    return data;
  },
  // 내 간판 조회 (userNo 단위) — 기업 상세에서 공유 시 자동 충전 타겟 판별용
  getMySignboard: async (_ctx, userNo) => {
    if (!userNo) return null;
    const { status, data } = await ServiceExec.get(signboard.getMy(userNo));
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  // 간판 최초 생성 (V2 이전 업체들 첫 진입 시)
  createSignboard: async (_ctx, businessNo) => {
    const { status, data } = await ServiceExec.post(signboard.create, { businessNo });
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  // 간판 이미지 업로드 (multipart) — businessNo + image File
  // 260429: 캐러셀 4:3(1200×900) 캔버스 → 260626: 세로 3:4 카드용 1080×1440 letterbox(#0a0a0a)
  // 260918 (대표): 캐러셀이 가로 4:3 cover 카드로 돌아오면서 구운 여백이 검정 띠로 노출 → 패딩 중단.
  //         일반 비즈니스 이미지(tryResize)와 같은 1600×1200 한도 비율 유지 축소 + JPG 82% 만. BE 도 같은 날 1200×900 패딩 삭제.
  uploadSignboardImage: async ({ dispatch }, { businessNo, file }) => {
    let payload = file;
    try {
      // forceReencode: 한도 안 작은 JPEG 도 캔버스 재인코딩 — EXIF 회전을 굽지 않으면 BE 썸네일(ImageIO)이 옆으로 눕는다
      payload = await resizeImageFile(file, { maxWidth: 1600, maxHeight: 1200, quality: 0.82, forceReencode: true });
    } catch (e) {
      console.warn('[uploadSignboardImage] 클라 리사이즈 실패, 원본 업로드:', e);
      payload = file;
    }
    const form = new FormData();
    form.append('businessNo', businessNo);
    form.append('image', payload);
    const { status } = await ServiceExec.multiPost(signboard.uploadImage, form);
    if (status !== ServiceExec.status.OK) return false;
    await dispatch('getSignboardDetail', businessNo);
    return true;
  },
  // 260428: 간판 노출 지역 단독 저장 (광역시도 또는 '전국'). 메인 캐러셀 칩과 연동.
  updateSignboardRegion: async (_, { businessNo, region }) => {
    const { status } = await ServiceExec.patch(signboard.updateRegion, { businessNo, region });
    return status === ServiceExec.status.OK;
  },
  // 260623: 간판 사인 — 카드 공지(마퀴) 저장 (260624: 글자색 / 260625: 배경색 동반)
  updateSignboardNotice: async (_, { businessNo, notice, noticeColor, bgColor }) => {
    const { status } = await ServiceExec.patch(signboard.updateNotice, { businessNo, notice, noticeColor, bgColor });
    return status === ServiceExec.status.OK;
  },
  // 260623: 간판 사인 — 상태 마크 CSV 저장
  updateSignMarks: async (_, { businessNo, marks }) => {
    const { status } = await ServiceExec.patch(signboard.updateSignMarks, { businessNo, marks });
    return status === ServiceExec.status.OK;
  },
  // 260429: 좋아요 페이지 발견용 — 본인이 안 누른 ACTIVE 가게 거리순 추천
  recommendLikeTargets: async (_, { lat, lng, limit = 6 } = {}) => {
    const params = { limit };
    if (lat != null && lng != null) { params.lat = lat; params.lng = lng; }
    const { status, data } = await ServiceExec.get(businessUser.recommendLikeTargets, params);
    if (status !== ServiceExec.status.OK) return [];
    return data ?? [];
  },
  // 260429: 알림(구독) 페이지 발견용 — 알림 받지 않는 ACTIVE 가게 거리순 추천
  recommendSubscribeTargets: async (_, { lat, lng, limit = 6 } = {}) => {
    const params = { limit };
    if (lat != null && lng != null) { params.lat = lat; params.lng = lng; }
    const { status, data } = await ServiceExec.get(businessUser.recommendSubscribeTargets, params);
    if (status !== ServiceExec.status.OK) return [];
    return data ?? [];
  },
  // 260428: 간판 상세 진입 시 클릭 이벤트 기록. 실패해도 화면 영향 X.
  recordSignboardClick: async (_ctx, { signboardNo, businessNo, source }) => {
    if (!signboardNo || !businessNo) return;
    try {
      const src = encodeURIComponent(source || 'DIRECT');
      const url = `${signboard.click(signboardNo)}?businessNo=${businessNo}&source=${src}`;
      await ServiceExec.post(url, {});
    } catch (e) {
      // 통계 실패는 사용자 화면에 영향 X
      console.warn('[recordSignboardClick]', e?.message);
    }
  },
  getAddressList: async ({ getters, commit }) => {
    const { userNo } = store.getters['user/getUserData'];
    const reqData = cloneDeep(getters['getAddressCondition']);
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.get(businessUser.getAddressList, reqData);
    if (status !== ServiceExec.status.OK) return;
    reqData.page = reqData.page + 1;
    commit('addressCondition', reqData);
    commit('addressPageInfo', data.pageInfo ?? {});
    commit('addressList', data.list ?? []);
  },
  getProductList: async ({ commit }, productCode) => {
    const { status, data } = await ServiceExec.get(businessUser.getProductList, { productCode });
    if (status !== ServiceExec.status.OK) return false;
    commit('productList', data);
    return true;
  },
  getBusiness: async ({ commit }, businessNo) => {
    let { userNo } = store.getters['user/getUserData'];

    if (isEmpty(userNo)) {
      userNo = -1;
    }
    const { status, data } = await ServiceExec.get(businessUser.getBusiness(businessNo, userNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('setDetail', data);
    return true;
  },
  // 260713 비회원 티저 — 게스트 앱 흐름 전용(PII 미포함 응답). 회원은 getBusiness 사용.
  getBusinessGuest: async ({ commit }, businessNo) => {
    const { status, data } = await ServiceExec.get(businessUser.getBusinessGuest(businessNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('setDetail', data);
    return true;
  },
  // 비즈니스 좋아요
  likeBusiness: async ({ commit }) => {
    const { userNo } = store.getters['user/getUserData'];
    const business = store.getters['business/getDetail'];
    // 260511: BE의 like API 응답에는 isSubscribed가 빠져있어 setDetail로 덮으면 알림 토글이 false로 떨어지는 버그.
    // 좋아요 토글 후에도 구독 상태는 유지되어야 하므로 클라 메모리의 isSubscribed를 보존해 patch.
    const prevSubscribed = business?.isSubscribed;
    const { status, data } = await ServiceExec.post(businessUser.likeBusiness(business?.businessNo, userNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('setDetail', { ...data, isSubscribed: prevSubscribed });
    return true;
  },

  // 비즈니스 구독 상태 조회 (종 버튼 상태)
  getSubscribeStatus: async ({ commit }, businessNo) => {
    const { userNo } = store.getters['user/getUserData'];
    if (isEmpty(userNo)) return false;
    try {
      const { status, data } = await ServiceExec.get(businessUser.subscribeBusiness(businessNo));
      console.log('[subscribe] GET status:', status, 'data:', data);
      if (status !== ServiceExec.status.OK) return false;
      commit('setDetailSubscribed', !!data?.isSubscribed);
      return true;
    } catch (e) {
      console.error('[subscribe] GET error:', e?.response?.status, e?.response?.data);
      return false;
    }
  },
  // 260520: 이벤트 발송 컨펌 모달 "구독중인 N분께" 표시용 단순 카운트
  getSubscriberCount: async (_ctx, businessNo) => {
    try {
      const { status, data } = await ServiceExec.get(businessUser.subscribeBusiness(businessNo));
      if (status !== ServiceExec.status.OK) return 0;
      return Number(data?.count ?? 0);
    } catch (e) {
      console.error('[subscribe] count error:', e?.response?.status, e?.response?.data);
      return 0;
    }
  },
  // 구독하기
  subscribeBusiness: async ({ commit }, businessNo) => {
    try {
      const { status, data } = await ServiceExec.post(businessUser.subscribeBusiness(businessNo));
      console.log('[subscribe] POST status:', status, 'data:', data);
      if (status !== ServiceExec.status.OK) return false;
      commit('setDetailSubscribed', true);
      // 260626 fix: 구독 직후 커피쿠폰 안내 제거. 260622에 BE가 구독 발급을 폐지(가입 시 발급으로 이동)했는데
      //   이 안내(260619, "BE가 구독 hook서 발급" 가정)만 잔존 → 구독마다 거짓 안내(발급 안 되는데 "쿠폰함에서 확인")가 떴음.
      return true;
    } catch (e) {
      console.error('[subscribe] POST error:', e?.response?.status, e?.response?.data);
      return false;
    }
  },
  // 구독 해제
  unsubscribeBusiness: async ({ commit }, businessNo) => {
    try {
      const { status } = await ServiceExec.delete(businessUser.subscribeBusiness(businessNo));
      console.log('[subscribe] DELETE status:', status);
      if (status !== ServiceExec.status.OK) return false;
      commit('setDetailSubscribed', false);
      return true;
    } catch (e) {
      console.error('[subscribe] DELETE error:', e?.response?.status, e?.response?.data);
      return false;
    }
  },

  getLikeList: async ({ commit, getters }) => {
    const reqData = cloneDeep(getters['getLikeCondition']);
    const pageInfo = getters['getLikePageInfo'];
    if (pageInfo.totalPage < reqData.page) return false;

    const { userNo } = store.getters['user/getUserData'];
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.get(businessUser.getLikeList, reqData);
    if (status !== ServiceExec.status.OK) return false;
    commit('likeList', data.list ?? []);
    reqData.page = reqData.page + 1;
    commit('likeCondition', reqData);
    commit('likePageInfo', data.pageInfo ?? {});
  },
  // 260428: 마케팅 알림 구독한 사업장 목록 — getLikeList와 같은 condition/pageInfo state 재사용 (단일 페이지)
  getSubscribedList: async ({ commit, getters }) => {
    const reqData = cloneDeep(getters['getLikeCondition']);
    const pageInfo = getters['getLikePageInfo'];
    if (pageInfo.totalPage < reqData.page) return false;

    const { userNo } = store.getters['user/getUserData'];
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.get(businessUser.getSubscribedList, reqData);
    if (status !== ServiceExec.status.OK) return false;
    commit('likeList', data.list ?? []);
    reqData.page = reqData.page + 1;
    commit('likeCondition', reqData);
    commit('likePageInfo', data.pageInfo ?? {});
  },
  getBusinessList: async ({ commit, getters }, params = {}) => {
    const pageInfo = getters['getPageInfo'];
    const reqData = cloneDeep(getters['getCondition']);
    reqData.isSearch = params.isSearch;
    reqData.searchText = getters['getSearchText'];

    // 260527: page>1 가드. 이 블록은 "자체 DB 페이지 소진 후 카카오만 추가"(무한스크롤) 의도인데
    //          page=1(새 검색/카테고리 진입)에서 발동하면 자체 DB fetch를 통째로 건너뛰고 ghost만 남는다.
    //          뒤로가기(NativeCall) 시 0건 fetch가 pageInfo.totalPage=0으로 오염시켜 page=1인데도 0<1 true로
    //          오발동 → 카테고리 재검색 시 등록 업체 사라지고 카카오 ghost만 노출되던 버그.
    if (reqData.page > 1 && pageInfo.totalPage < reqData.page) {
      // 260522: 카테고리 검색 시에도 카카오 mix-in 허용 (ghost 같이 노출). 카테고리 핸들러가 searchText를 카테고리명으로 set해두기 때문에 getKakaoAdd의 query로 자연스럽게 카테고리명이 들어감.
      if (isEmpty(params.userNo) && params.isKakao) {
        await store.dispatch('common/getKakaoAdd', reqData);
        reqData.page = reqData.page + 1;
        commit('conditionPage', reqData.page);
      }
      return false;
    }

    const { latitude, longitude } = getters['getCoordinate'];
    reqData.latitude = latitude;
    reqData.longitude = longitude;
    // 260730: 거리(m) 원점 = 항상 최신 coordinate.
    //   BE getBusinessList는 distance를 fixLatitude/fixLongitude로만 계산(BusinessDao.getBusinessList).
    //   fixCoordinate는 selectAddress/getSelectAddress(저장주소)만 채우고, 재중심 4경로
    //   (Util.getCurrentLocation / NativeCall.setNativeLocation / HeaderLocation.addressSetting /
    //    business/index.vue searchModal)는 coordinate만 갱신 → fixCoordinate가 저장주소에 stale 고정.
    //   결과: 탐색에서 '내 위치' 재측위 시 캐러셀(getFeed=latitude/longitude)만 갱신되고
    //   리스트 카드 m 표시는 그대로였다. 저장주소 경로는 coordinate도 같은 값으로 커밋하므로 동작 동일.
    reqData.fixLatitude = latitude;
    reqData.fixLongitude = longitude;
    // if (!isEmpty(params.isOneKM)) reqData.isOneKM = params.isOneKM;
    reqData.isOneKm = false;
    if (!isEmpty(params.userNo)) reqData.userNo = params.userNo;
    if (!isEmpty(params.isEvent)) reqData.isEvent = params.isEvent;
    if (!isEmpty(params.isDonation)) reqData.isDonation = params.isDonation;
    if (!isEmpty(params.isJob)) reqData.isJob = params.isJob;

    // 260522: searchText 있고 첫 페이지 + 메인 피드 호출(userNo 없음)일 때 ghost mix-in 엔드포인트
    // (isSearch=true 인 검색 모달 경로도 ghost 노출 — 별도 state라 businessSearchList와 독립)
    const useGhost = !isEmpty(reqData.searchText) && reqData.page === 1 && isEmpty(params.userNo);
    const apiPath = useGhost ? businessUser.getBusinessListWithGhost : businessUser.getBusinessList;

    const { status, data } = await ServiceExec.get(apiPath, reqData);

    if (status !== ServiceExec.status.OK) return false;

    // ghost 엔드포인트면 { regular: { list, pageInfo }, ghost: [...] } 구조. 기존 엔드포인트면 { list, pageInfo }
    const regularData = useGhost ? (data?.regular ?? {}) : data;
    const ghostList = useGhost ? (data?.ghost ?? []) : [];

    // 260523: page>1 + 빈 결과 = 페이지 끝. mutation이 빈 array를 wipe로 해석하기 때문에
    //          이 경우 commit 자체를 skip해서 기존 list 보존. page=1 fetch는 정상 reset 의도라 그대로 진행.
    //          (categorySearch에서 condition.page=1 reset 후 fetch → page++됨. 백키 후 재 fetch가 page=2여서
    //           카테고리 매장이 적은 케이스에 빈 결과 → businessSearchList 통째 wipe되던 버그)
    if (params.isSearch) {
      const searchList = regularData.list ?? [];
      if (!(reqData.page > 1 && searchList.length === 0)) {
        commit('businessSearchList', searchList);
      }
    }
    else commit('businessList', {
      isFlag: isEmpty(params.userNo),
      businessList: regularData.list ?? [],
      // 260429: 마이페이지 호출일 때 owner 가드 — race로 메인 피드 결과가 끼는 것 차단
      expectedUserNo: !isEmpty(params.userNo) ? params.userNo : null
    });
    // ghost는 page=1 진입 시에만 갱신 (페이지네이션으로 다음 페이지 로드 시 ghost는 그대로 유지하기 위함은 아님 —
    // 어차피 useGhost=false라 ghostList 변경 없음. 첫 페이지 진입 시에만 commit).
    if (useGhost) commit('ghostList', ghostList);
    // 260522: 카테고리 검색 시에도 카카오 mix-in 허용 (위 동일).
    if (isEmpty(params.userNo) && params.isKakao) {
      await store.dispatch('common/getKakaoAdd', reqData);
    }

    reqData.page = reqData.page + 1;
    commit('condition', reqData);
    commit('pageInfo', regularData.pageInfo ?? {});
    return true;
  },
  // 260429: 마이페이지 전용 — JWT principal로 BE가 owner 강제. 메인 피드 state와 완전 격리.
  getMyBusinessList: async ({ commit, getters }) => {
    const pageInfo = getters['getMyPageInfo'];
    const reqData = cloneDeep(getters['getMyCondition']);
    if (pageInfo.totalPage && pageInfo.totalPage < reqData.page) return false;

    const { userNo } = store.getters['user/getUserData'];
    if (isEmpty(userNo)) return false;

    const { status, data } = await ServiceExec.get(businessUser.getMyBusinessList, reqData);
    if (status !== ServiceExec.status.OK) return false;

    commit('myBusinessList', { myBusinessList: data.list ?? [], expectedUserNo: userNo });
    reqData.page = reqData.page + 1;
    commit('myCondition', reqData);
    commit('myPageInfo', data.pageInfo ?? {});
    return true;
  },
  // 검색시 하단에 해당 단어와 매칭되는 비즈니스들 목록 보여주기 위한 리스트 가져오기
  getSearchPreview: async ({ commit, getters }, searchText) => {
    const reqData = { ...defaultPaging };
    reqData.searchText = searchText;
    reqData.isOneKM = false;
    const { latitude, longitude } = getters['getCoordinate'];
    reqData.latitude = latitude;
    reqData.longitude = longitude;
    // 260730: getBusinessList와 동일 — 거리 원점은 항상 최신 coordinate (stale fixCoordinate 우선 제거)
    reqData.fixLatitude = latitude;
    reqData.fixLongitude = longitude;

    const { status, data } = await ServiceExec.get(businessUser.getBusinessList, reqData);
    if (status !== ServiceExec.status.OK) return false;
    commit('businessPreviewList', data.list ?? []);
    trackFeature('search');
    logSearch(searchText, (data.list ?? []).length);
    return true;
  },

  // 사업자 번호 유효성 검사
  validateLicenseNumber: async ({ commit }, licenseNumber) => {
    const { status, data } = await ServiceExec.get(businessUser.licenseNumberValid(licenseNumber));
    if (status !== ServiceExec.status.OK) return { status: status, data: data };
    return { status: status, data: data };
  },
  saveBusiness: async ({ getters }) => {
    const reg = getters['getRegister'];
    const params = cloneDeep(reg);
    const { userNo } = store.getters['user/getUserData'];
    params.tags = params.tags?.join(',');
    params.offDays = (params.offDays || []).join(',');
    params.userNo = userNo;
    params.isSave = true;
    // 260515 P0 fix: 신규 등록 path인데 reg state에 이전 businessNo가 남아있으면 BE가 UPDATE로 빠져
    // 기존 비즈니스를 덮어쓰는 사고(qa03 테스트20→테스트26). BE 가드와 동시에 FE도 강제 null.
    delete params.businessNo;
    // 260428 오후회의2: 비즈니스 등록 이미지도 클라 리사이즈 (cloneDeep으로 깨진 File은 reg 원본에서 다시 가져와 덮음)
    if (Array.isArray(reg.imageList)) {
      params.imageList = await tryResizeList(reg.imageList.filter((f) => f instanceof File));
    }
    if (reg.logoImage instanceof File) {
      params.logoImage = await tryResize(reg.logoImage);
    }
    const { status, data } = await ServiceExec.multiPost(businessUser.saveBusiness, asFormData(params));
    if (status !== ServiceExec.status.OK) {
      // 260519: BE ServiceException 메시지(body=string) 그대로 모달 노출.
      const msg = typeof data === 'string' ? data : (data?.message || '등록에 실패했습니다. 다시 시도해주세요.');
      showTextModal(msg, '확인');
      return false;
    }
    return data;
  },
  updateBusiness: async ({ getters }) => {
    const reg = getters['getRegister'];
    const params = cloneDeep(reg);
    const detail = getters['getDetail'];
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;

    params.tags = (params.tags || []).join(',') || '';
    params.offDays = (params.offDays || []).join(',') || '';
    params.isSave = true;
    // 기존 이미지(FileDto)와 새 이미지(File) 분리
    const existingImages = (detail.imageList || []).filter(img => img && img.fileNo);
    if (!isEmpty(params.deleteList)) {
      params.remainImageList = existingImages.filter(
        image => !params.deleteList.some(del => del.fileNo === image.fileNo)
      );
      // 260519: multipart 빈 array는 BE에 null로 도착. 모두 삭제 의도일 때 명시 flag 전송.
      if (params.remainImageList.length === 0) {
        params.imagesCleared = true;
      }
      delete params.deleteList;
    } else {
      params.remainImageList = existingImages;
    }
    // 새 이미지만 (File 객체만) — cloneDeep 전 원본에서 참조
    // 260428 오후회의2: 새 File들 클라 리사이즈 (1600×1200 / JPG 82%)
    if (!isEmpty(reg.imageList)) {
      const newFiles = reg.imageList.filter(image => !image.fileNo && image instanceof File);
      params.imageList = await tryResizeList(newFiles);
    }
    // logoImage: cloneDeep이 File 객체를 깨뜨리므로 원본 참조
    if (reg.logoImage instanceof File) {
      params.logoImage = await tryResize(reg.logoImage);
      params.deleteLogoImage = { fileNo: 0 };
    } else {
      // 기존 이미지 유지 또는 삭제 — MultipartFile로 보내면 안됨
      delete params.logoImage;
    }

    // 260526 대표사진(저장통합): imageList[0]=대표. 기존 이미지면 mainFileNo, 이번 업로드 신규면 mainNewIndex=0(맨 앞).
    // BE updateGallery가 저장 후 order 재배정(main=0)+썸네일 미러. FE reorder만으론 재조회 원복이라 필수.
    const mainImg = (reg.imageList || [])[0];
    if (mainImg) {
      if (mainImg.fileNo) params.mainFileNo = mainImg.fileNo;
      else if (mainImg instanceof File) params.mainNewIndex = 0;
    }

    const { status, data } = await ServiceExec.multiPut(businessUser.updateBusiness, asFormData(params));
    if (status !== ServiceExec.status.OK) {
      // 260519: BE ServiceException 메시지(body=string) 그대로 모달 노출. UX 명확화.
      const msg = typeof data === 'string' ? data : (data?.message || '저장에 실패했습니다. 다시 시도해주세요.');
      showTextModal(msg, '확인');
      return false;
    }
    return data;
  },
  // 사업자등록번호 검증 (상세 응답)
  verifyLicense: async (_, licenseNumber) => {
    const { status, data } = await ServiceExec.post(businessUser.verifyLicense, { licenseNumber });
    if (status !== ServiceExec.status.OK) return { verified: false, message: '인증 요청에 실패했습니다.' };
    return data;
  },

  // 간소화 비즈니스 등록
  registerSimpleBusiness: async (_, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = (await ServiceExec.post(businessUser.registerSimple, params)) || {}; // 네트워크 끊김 = undefined
    if (status !== ServiceExec.status.OK) {
      // 261001 대표 제보 '등록 완료를 눌러도 등록 안 됨' — 서버가 거절 사유(사업자번호 충돌 등)를 보냈는데 화면이 아무 말 없이 끝났다.
      showTextModal(errorText(data, '사업장을 등록하지 못했습니다. 잠시 후 다시 시도해 주십시오.'), '확인');
      return false;
    }
    return data;
  },

  saveAddress: async ({ getters }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.saveAddress, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  removeAddress: async ({ getters }, addressNo) => {
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    const { status, data } = await ServiceExec.delete(businessUser.removeAddress(addressNo));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  removeBusiness: async ({ getters }, businessNo) => {
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    const { status, data } = await ServiceExec.delete(businessUser.removeBusiness(businessNo));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  reportBusiness: async ({ getters }, params) => {
    const { userNo, name } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    params.userNo = userNo;
    params.reporterName = name;
    const { status, data } = await ServiceExec.post(businessUser.reportBusiness, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  // 주소 선택
  selectAddress: async ({ commit }, addressNo) => {
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    const { status, data } = await ServiceExec.put(businessUser.selectAddress(userNo, addressNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('selectAddress', data);
    commit('coordinate', { latitude: data.latitude, longitude: data.longitude });
    commit('fixCoordinate', { latitude: data.latitude, longitude: data.longitude });
    return true;
  },

  // 선택한 주소 목록
  getSelectAddress: async ({ commit }) => {
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) return false;
    const { status, data } = await ServiceExec.get(businessUser.getSelectAddress(userNo));
    if (status !== ServiceExec.status.OK) return false;
    if (data) {
      commit('selectAddress', data || {});
      commit('coordinate', { latitude: data.latitude, longitude: data.longitude } || {});
      commit('fixCoordinate', { latitude: data.latitude, longitude: data.longitude } || {});
    }
    return data;
  },
  saveMarketing: async ({ getters }) => {
    const reqData = cloneDeep(getters['getMarketingRegister']);
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.multiPost(businessUser.saveMarketing, asFormData(reqData));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  // 마케팅 수정
  updateMarketing: async ({ getters }) => {
    const params = cloneDeep(getters['getMarketingRegister']);
    const { userNo } = store.getters['user/getUserData'];
    const detail = getters['getDetail'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    params.userNo = userNo;
    if (!isEmpty(params.deleteList)) {
      const filteredList = detail.imageList.filter(
        image =>
          !(image instanceof File || image instanceof Blob) &&
          !params.deleteList.some(del => del.fileNo === image.fileNo)
      );
      params.remainImageList = filteredList;
      delete params.deleteList;
    } else {
      params.remainImageList = detail.imageList.filter(image => !(image instanceof File || image instanceof Blob));
    }
    if (!isEmpty(params.imageList)) {
      params.imageList = params.imageList.filter(image => !image.fileNo);
    }

    const { status, data } = await ServiceExec.multiPut(businessUser.updateMarketing, asFormData(params));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  // 마케팅 상세
  getMarketing: async ({ commit }, marketingNo) => {
    const { status, data } = await ServiceExec.get(businessUser.getMarketing(marketingNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('setDetail', data);
    return true;
  },
  // 이벤트 알림권 남은 수 ( 푸시 가능 수 )
  marketingTicketCount: async ({ commit }) => {
    const { userNo } = store.getters['user/getUserData'];
    const { status, data } = await ServiceExec.get(businessUser.marketingTicketCount(userNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('marketingTicketCount', data);
    return true;
  },
  // 마케팅 구매 내역
  getMarketingPurchaseList: async ({ commit, getters }) => {
    const reqData = cloneDeep(getters['getPurchaseCondition']);
    const pageInfo = getters['getPurchasePageInfo'];
    if (pageInfo.totalPage < reqData.page) return false;

    const { userNo } = store.getters['user/getUserData'];
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.get(businessUser.getMarketingPurchaseList, reqData);
    if (status !== ServiceExec.status.OK) return false;
    commit('purchaseList', data.list ?? []);
    reqData.page = reqData.page + 1;
    commit('purchaseCondition', reqData);
    commit('purchasePageInfo', data.pageInfo ?? {});
    return true;
  },
  // 사용 내역
  getTicketUseList: async ({ commit, getters }) => {
    const reqData = cloneDeep(getters['getPurchaseCondition']);
    const pageInfo = getters['getPurchasePageInfo'];
    if (pageInfo.totalPage < reqData.page) return false;

    const { userNo } = store.getters['user/getUserData'];
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.get(businessUser.getTicketUseList, reqData);
    if (status !== ServiceExec.status.OK) return false;
    commit('purchaseList', data.list ?? []);
    reqData.page = reqData.page + 1;
    commit('purchaseCondition', reqData);
    commit('purchasePageInfo', data.pageInfo ?? {});
    return true;
  },
  // 이벤트 알림권 사용처리
  ticketUse: async ({ commit }, request) => {
    const { userNo } = store.getters['user/getUserData'];
    const { status, data } = await ServiceExec.post(
      businessUser.ticketUse(userNo, request.businessNo, request.isPoint)
    );
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  // 이벤트 알림권 재구매
  ticketRePurchase: async ({ commit }, reqData) => {
    const { userNo } = store.getters['user/getUserData'];
    const { status, data } = await ServiceExec.post(
      businessUser.ticketRePurchase(userNo, reqData.businessNo, reqData.isPoint)
    );
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  reportMarketing: async ({ commit }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.reportMarketing, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  //  푸시 전송
  sendPush: async ({ commit }, marketingNo) => {
    const { status, data } = await ServiceExec.post(businessUser.sendPush(marketingNo));
    if (status !== ServiceExec.status.OK) {
      showTextModal(data, '확인');
      return false;
    }
    return data;
  },
  // 이미지 등록권 구매 후 임시저장 ( 구매 후 비즈니스 등록을 안해버리면 해당 등록권이 사라질 수 있기 때문 )
  temporaryImageSave: async ({ commit }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.temporarySave, params);
    if (status !== ServiceExec.status.OK) {
      showTextModal(data, '확인');
      return false;
    }
    return data;
  },
  // 이미지 최대 등록 가능 갯수 가져오기
  imageMaxSize: async ({ commit }, params) => {
    const { status, data } = await ServiceExec.get(businessUser.imageMaxSize, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  imageTicketUse: async ({ commit }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.marketingImageTicketUse, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  donationImageTicketUse: async ({ commit }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.donationImageTicketUse, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  getDonation: async ({ commit }, donationNo) => {
    const { status, data } = await ServiceExec.get(businessUser.getDonation(donationNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('setDetail', data);
    return true;
  },
  saveDonation: async ({ getters }) => {
    const reqData = cloneDeep(getters['getDonationRegister']);
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.multiPost(businessUser.saveDonation, asFormData(reqData));
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  updateDonation: async ({ getters }) => {
    const params = cloneDeep(getters['getDonationRegister']);
    const { userNo } = store.getters['user/getUserData'];
    const detail = getters['getDetail'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    params.userNo = userNo;
    if (!isEmpty(params.deleteList)) {
      const filteredList = detail.imageList.filter(
        image =>
          !(image instanceof File || image instanceof Blob) &&
          !params.deleteList.some(del => del.fileNo === image.fileNo)
      );
      params.remainImageList = filteredList;
      delete params.deleteList;
    } else {
      params.remainImageList = detail.imageList.filter(image => !(image instanceof File || image instanceof Blob));
    }
    if (!isEmpty(params.imageList)) {
      params.imageList = params.imageList.filter(image => !image.fileNo);
    }

    const { status, data } = await ServiceExec.multiPut(businessUser.updateDonation, asFormData(params));
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  donationSendPush: async ({ commit }, donationNo) => {
    const { status, data } = await ServiceExec.post(businessUser.donationSendPush(donationNo));
    if (status !== ServiceExec.status.OK) {
      showTextModal(data, '확인');
      return false;
    }
    return data;
  },
  reportDonation: async ({ commit }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.reportDonation, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  getJob: async ({ commit }, jobNo) => {
    const { status, data } = await ServiceExec.get(businessUser.getJob(jobNo));
    if (status !== ServiceExec.status.OK) return false;
    commit('setJobDetail', data);
    return true;
  },
  saveJob: async ({ getters }) => {
    const reqData = cloneDeep(getters['getJobRegister']);
    const { userNo } = store.getters['user/getUserData'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    reqData.userNo = userNo;
    const { status, data } = await ServiceExec.multiPost(businessUser.saveJob, asFormData(reqData));
    console.log(status, data)
    if (status !== ServiceExec.status.OK) return data;
    return data;
  },
  updateJob: async ({ getters }) => {
    const params = cloneDeep(getters['getJobRegister']);
    const { userNo } = store.getters['user/getUserData'];
    const detail = getters['getDetail'];
    if (!userNo) {
      showTextModal('해당 기능은 로그인이 필요합니다.', '확인');
      return false;
    }
    params.userNo = userNo;
    const { status, data } = await ServiceExec.multiPut(businessUser.updateJob, asFormData(params));
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  jobSendPush: async ({ commit }, jobNo) => {
    const { status, data } = await ServiceExec.post(businessUser.jobSendPush(jobNo));
    if (status !== ServiceExec.status.OK) {
      showTextModal(data, '확인');
      return false;
    }
    return data;
  },
  reportJob: async ({ commit }, params) => {
    const { userNo } = store.getters['user/getUserData'];
    params.userNo = userNo;
    const { status, data } = await ServiceExec.post(businessUser.reportJob, params);
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  deleteJob: async ({ commit }, jobNo) => {
    const { status, data } = await ServiceExec.delete(businessUser.deleteJob(jobNo));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  deleteDonation: async ({ commit }, donationNo) => {
    const { status, data } = await ServiceExec.delete(businessUser.deleteDonation(donationNo));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },
  deleteMarketing: async ({ commit }, marketingNo) => {
    const { status, data } = await ServiceExec.delete(businessUser.deleteMarketing(marketingNo));
    if (status !== ServiceExec.status.OK) return false;
    return data;
  },

  // 260428: 통계 그리드 — 클릭/시간대/히트맵을 한 번에 묶어 반환
  getSignboardStats: async (_ctx, { signboardNo, period = 'LAST_7_DAYS' } = {}) => {
    if (!signboardNo) return null;
    const [click, hourly, heatmap] = await Promise.all([
      ServiceExec.get(signboardStats.clickToday(signboardNo)),
      ServiceExec.get(signboardStats.hourly(signboardNo), { period }),
      ServiceExec.get(signboardStats.heatmap(signboardNo), { period })
    ]);
    const ok = r => r?.status === ServiceExec.status.OK;
    const buckets = ok(hourly) ? (hourly.data?.buckets ?? []) : [];
    const cells   = ok(heatmap) ? (heatmap.data?.cells ?? []) : [];
    let hourlyMax = 0, hourlyPeakHr = null;
    for (const b of buckets) {
      const c = b?.count ?? 0;
      if (c > hourlyMax) { hourlyMax = c; hourlyPeakHr = b.hr; }
    }
    const heatmapTop = [...cells].sort((a, b) => (b?.count ?? 0) - (a?.count ?? 0))
      .filter(c => (c?.count ?? 0) > 0).slice(0, 3)
      .map(c => ({ dow: c.dow, hr: c.hr, count: c.count }));
    return {
      clickToday: ok(click) ? click.data : null,
      hourly: buckets.map(b => ({ hr: b.hr, count: b.count ?? 0 })),
      hourlyMax,
      hourlyPeakHr,
      heatmapTop
    };
  },

  // 260428: 통계 그리드 5/6/8/9 (주간 공유/충전시간/좋아요/구독자)
  getSignboardMetrics: async (_ctx, { signboardNo, period = 'LAST_7_DAYS' } = {}) => {
    if (!signboardNo) return null;
    const { status, data } = await ServiceExec.get(signboardStats.metrics(signboardNo), { period });
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },

  // 260428: 노출 통합 (누적/오늘/도달/시간대 분포/히트맵 TOP3)
  getSignboardImpression: async (_ctx, { signboardNo, period = 'LAST_7_DAYS' } = {}) => {
    if (!signboardNo) return null;
    const { status, data } = await ServiceExec.get(signboardStats.impression(signboardNo), { period });
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },

  // 260428: 타겟율 (클릭/노출 %)
  getSignboardTargetRate: async (_ctx, { signboardNo, period = 'LAST_7_DAYS' } = {}) => {
    if (!signboardNo) return null;
    const { status, data } = await ServiceExec.get(signboardStats.targetRate(signboardNo), { period });
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },

  // 260920 최종 리뷰 — 온라인 간판 24h 라인은 '오늘 클릭'(click-hourly, 시드 제외 실클릭)로. hourly/heatmap 은 배터리 공유 기반이라 ① 상시 ON 이후 0 이 된다.
  getSignboardClickHourly: async (_ctx, { signboardNo } = {}) => {
    if (!signboardNo) return null;
    const { status, data } = (await ServiceExec.get(signboardStats.clickHourly(signboardNo))) || {};
    if (status !== ServiceExec.status.OK) return null;
    const buckets = Array.isArray(data?.buckets) ? data.buckets : [];
    return buckets.map(b => ({ hr: Number(b.hr), count: Number(b.cnt ?? b.count ?? 0) }));
  },

  // 260919 통합 통계 — 현판 축. 403 FRANCHISE_REQUIRED 는 S1(안내 카드) 분기용으로 code 를 살려 돌려준다(throw 없음).
  getQrBusinessStats: async (_ctx, { businessNo, period = 'LAST_7_DAYS' } = {}) => {
    if (!businessNo) return { ok: false, status: 0, code: 'NO_BUSINESS' };
    const res = (await ServiceExec.get(qrUser.stats(businessNo), { period })) || {};
    if (res.status === ServiceExec.status.OK) return { ok: true, data: res.data };
    return { ok: false, status: res.status, code: res.data?.code || null };
  },

  // 260919 무료 현판(가맹점) 신청 — BE ServiceException 은 500 평문 body 라 message 로 넘긴다.
  submitFranchiseRequest: async (_ctx, payload) => {
    const res = (await ServiceExec.post(qrUser.franchiseRequest, payload)) || {};
    if (res.status === ServiceExec.status.OK) return { ok: true, message: '' };
    return { ok: false, message: typeof res.data === 'string' ? res.data : '신청에 실패했습니다. 잠시 후 다시 시도해주세요.' };
  },

  // 260923 대표 회의: 현판 사전 신청(연락처·상호). 무인증 공개 API — 같은 번호 24시간 재신청은 서버가 조용히 흡수(200).
  //   BE ServiceException(형식 오류·폭주 차단)은 500 평문 body 라 message 로 넘긴다(submitFranchiseRequest 와 같은 계약).
  //   261103 유튜버 구독 선물(entry '구독선물:<코드>')은 응답에 그 채널 사전 신청 번호(number)가 온다 — 다른 신청은 없음(null).
  submitQrPreOrder: async (_ctx, payload) => {
    const res = (await ServiceExec.post(qrUser.preOrder, payload)) || {};
    if (res.status === ServiceExec.status.OK) return { ok: true, message: '', number: Number(res.data?.number) || null };
    return { ok: false, message: typeof res.data === 'string' && res.data ? res.data : '신청에 실패했습니다. 잠시 후 다시 시도해 주세요.' };
  },

  getFranchiseRequestStatus: async (_ctx, businessNo) => {
    if (!businessNo) return null;
    const { status, data } = (await ServiceExec.get(qrUser.franchiseStatus, { businessNo })) || {};
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },

  // ===== 260608 기업↔기업 B2B 연결보드 ("원합니다/구합니다") =====
  // 설계서: docs/0605_원합니다_구합니다_B2B연결보드_설계서.md
  // 화면 로컬 state로 다루므로(구인/협업 패턴) 액션은 fetch/return만. userNo는 BE가 JWT로 강제 주입.

  // 카테고리+하위옵션 목록 (비로그인 허용)
  getB2bCategories: async () => {
    const { status, data } = await ServiceExec.get(b2bBoard.category);
    if (status !== ServiceExec.status.OK) return [];
    return Array.isArray(data) ? data : [];
  },
  // 공개 목록 (비로그인 허용). params: { categoryCode, direction, keyword, page, size }
  getB2bPostList: async (_ctx, params = {}) => {
    const { status, data } = await ServiceExec.get(b2bBoard.postList, params);
    if (status !== ServiceExec.status.OK) return null;
    return data; // { list, pageInfo } 형태 기대
  },
  // 상세 (viewCount++, 비로그인 허용)
  getB2bPost: async (_ctx, postNo) => {
    const { status, data } = await ServiceExec.get(b2bBoard.post(postNo));
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  // 등록 (로그인+사업자). userNo는 BE에서 JWT 주입 — payload에 넣지 않음.
  saveB2bPost: async (_ctx, payload) => {
    const { status, data } = await ServiceExec.post(b2bBoard.savePost, payload);
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '등록에 실패했습니다. 다시 시도해주세요.');
      showTextModal(msg, '확인');
      return null;
    }
    return data; // { postNo }
  },
  // 수정 (로그인+소유권). userNo는 BE에서 JWT 주입 — payload에 넣지 않음. { postNo, payload }
  updateB2bPost: async (_ctx, { postNo, payload }) => {
    const { status, data } = await ServiceExec.put(b2bBoard.updatePost(postNo), payload);
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '수정에 실패했습니다. 다시 시도해주세요.');
      showTextModal(msg, '확인');
      return false;
    }
    return data ?? true;
  },
  // 글 사진 등록/교체 (multipart 'image'). { postNo, file }
  uploadB2bPostImage: async (_ctx, { postNo, file }) => {
    const fd = new FormData();
    fd.append('image', file);
    const { status } = await ServiceExec.multiPost(b2bBoard.postImage(postNo), fd);
    return status === ServiceExec.status.OK;
  },
  // 글 사진 삭제. postNo
  deleteB2bPostImage: async (_ctx, postNo) => {
    const { status } = await ServiceExec.delete(b2bBoard.postImage(postNo));
    return status === ServiceExec.status.OK;
  },
  // 내 글 목록 (로그인)
  getMyB2bPosts: async (_ctx, params = {}) => {
    const { status, data } = await ServiceExec.get(b2bBoard.myPosts, params);
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  // 내 소유 업체를 대상(targetBusinessNo)으로 지정한 받은 도움 요청 목록 (로그인). params: { page, size }
  getB2bReceivedWants: async (_ctx, params = {}) => {
    const { status, data } = await ServiceExec.get(b2bBoard.receivedWants, params);
    if (status !== ServiceExec.status.OK) return [];
    return data;
  },
  // 상태 변경 (로그인+소유권). body{status}
  updateB2bPostStatus: async (_ctx, { postNo, status: nextStatus }) => {
    const { status, data } = await ServiceExec.patch(b2bBoard.postStatus(postNo), { status: nextStatus });
    if (status !== ServiceExec.status.OK) return false;
    return data ?? true;
  },
  // 문의 보내기 (로그인+사업자). applicantUserNo는 BE에서 JWT 주입.
  sendB2bInquiry: async (_ctx, payload) => {
    const { status, data } = await ServiceExec.post(b2bBoard.inquiry, payload);
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '문의 전송에 실패했습니다. 다시 시도해주세요.');
      showTextModal(msg, '확인');
      return null;
    }
    return data; // { inquiryNo }
  },
  // 내 글에 온 문의 목록 (로그인). params: { postNo? }
  getB2bInquiriesReceived: async (_ctx, params = {}) => {
    const { status, data } = await ServiceExec.get(b2bBoard.inquiryReceived, params);
    if (status !== ServiceExec.status.OK) return [];
    return Array.isArray(data) ? data : [];
  },
  // 문의 상세 + 자동 읽음 (로그인+소유권)
  getB2bInquiryDetail: async (_ctx, inquiryNo) => {
    const { status, data } = await ServiceExec.get(b2bBoard.inquiryDetail(inquiryNo));
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },

  // ===== 260608 이벤트 가입(커피 쿠폰) — 골격 =====
  // memory: project_coffee_coupon_event_260608. 발급 ↔ 차감(직원 확인) 분리. userNo는 BE가 JWT로 강제 주입.

  // 260923 대표: 메가커피 이벤트 중단 — 받는 경로용 액션(이벤트 가게 목록·발급·내 공유코드·추천코드 입력·코드 검증) 삭제.
  //   아래는 이미 받은 쿠폰(쿠폰함, 11/02 만료)과 운영진 발급 패널이 쓰는 것만 남긴다.
  // 내 쿠폰 목록 (로그인)
  getMyEventCoupons: async () => {
    const { status, data } = await ServiceExec.get(eventCoupon.myCoupons);
    if (status !== ServiceExec.status.OK) return [];
    return Array.isArray(data) ? data : [];
  },
  // 직원 확인 → 소멸 (로그인+본인 쿠폰). 성공 시 갱신된 쿠폰(REDEEMED) 반환.
  redeemEventCoupon: async (_ctx, couponNo) => {
    const { status, data } = await ServiceExec.patch(eventCoupon.redeem(couponNo), {});
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '사용 처리에 실패했습니다.');
      showTextModal(msg, '확인');
      return null;
    }
    return data; // 갱신된 쿠폰
  },
  // 260618 쿠폰함 '지우기' → 소프트 숨김 (로그인+본인 쿠폰). 성공 시 true.
  hideEventCoupon: async (_ctx, couponNo) => {
    const { status, data } = await ServiceExec.patch(eventCoupon.hide(couponNo), {});
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '삭제에 실패했습니다.');
      showTextModal(msg, '확인');
      return false;
    }
    return true;
  },

  // ===== 260915 운영진(와쳐) 메가커피 쿠폰 발급/반납 =====
  // 패널 상태 — 실패는 모달 없이 null(비와쳐 화면에 에러 안 띄움). { watcher, remaining, liveCouponNo }
  getEventWatchStatus: async () => {
    const { status, data } = await ServiceExec.get(eventCoupon.watchStatus);
    if (status !== ServiceExec.status.OK) return null;
    return data;
  },
  // 발급 — 성공 시 발급된 쿠폰, 실패(풀 소진·보유 중·비와쳐) 시 BE 메시지 모달 + null
  issueEventWatchCoupon: async () => {
    const { status, data } = await ServiceExec.post(eventCoupon.watchIssue, {});
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '쿠폰 발급에 실패했습니다. 다시 시도해주세요.');
      showTextModal(msg, '확인');
      return null;
    }
    return data;
  },
  // 반납 — 성공 시 true, 실패 시 BE 메시지 모달 + false
  returnEventWatchCoupon: async (_ctx, couponNo) => {
    const { status, data } = await ServiceExec.patch(eventCoupon.watchReturn(couponNo), {});
    if (status !== ServiceExec.status.OK) {
      const msg = typeof data === 'string' ? data : (data?.message || '반납 처리에 실패했습니다.');
      showTextModal(msg, '확인');
      return false;
    }
    return true;
  }
};
